import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Upload, X, Check, ChevronRight, ChevronLeft, User, Briefcase, GraduationCap, Award, FileText, Languages, Building2, MapPin, AlertCircle, Info, ChevronDown } from 'lucide-react';
import api from '../services/api';
import { calculateProfileCompletion } from '../utils/profileCompletion';

const OCCUPATIONS = [
  'Nurse',
  'Physiotherapist',
  'Radiology Assistant',
  'Laboratory Technician',
  'Dentist',
  'OTA / ATA',
  'Pharmacist',
  'Mechatronics Technicians',
  'Bus / Truck Drivers',
  'Ausbildung',
  'Others'
];

const LANGUAGE_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'Native'];

const UploadCard = ({ label, required, file, onChange, accept = ".pdf,.doc,.docx,.jpg,.jpeg,.png", existingUrl = null }) => {
  const fileInput = useRef(null);
  const hasFile = file || existingUrl;

  return (
    <div className={`border-2 border-dashed rounded-2xl p-6 transition-all duration-300 cursor-pointer ${hasFile
      ? 'border-blue-400 bg-blue-50/50 hover:border-blue-500'
      : 'border-slate-300 bg-slate-50 hover:border-blue-400 hover:bg-blue-50'
      }`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-sm font-bold text-slate-800">
            {label} {required && <span className="text-red-500">*</span>}
          </div>
          <div className="text-[10px] font-medium text-slate-500 mt-0.5 tracking-tight uppercase">Accepted: PDF, DOC, DOCX, JPG, PNG — max 10MB</div>
        </div>
        {hasFile && (
          <div className="flex items-center space-x-2 bg-blue-100 px-2 py-1 rounded-lg">
            <Check className="h-4 w-4 text-blue-600" />
            <span className="text-[10px] text-blue-600 font-bold uppercase tracking-wider">Ready</span>
          </div>
        )}
      </div>
      <label className="flex flex-col items-center justify-center min-h-[140px] cursor-pointer group">
        <div className="text-center">
          <div className={`mx-auto mb-4 p-3 rounded-2xl transition-all duration-300 group-hover:scale-110 ${hasFile ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400 group-hover:bg-blue-100 group-hover:text-blue-600'
            }`}>
            <Upload className="h-8 w-8" />
          </div>
          {file ? (
            <div className="text-sm font-bold text-slate-800 animate-in fade-in duration-300">{file.name}</div>
          ) : existingUrl ? (
            <div className="text-sm font-bold text-blue-600">Document Uploaded</div>
          ) : (
            <div className="text-sm font-medium text-slate-600">Click to upload or drag and drop</div>
          )}
          {existingUrl && !file && (
            <a
              href={existingUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-xs font-bold text-blue-600 hover:text-blue-700 mt-2 flex items-center justify-center gap-1 hover:underline"
            >
              <FileText className="h-3 w-3" />
              Preview Current
            </a>
          )}
        </div>
        <input
          ref={fileInput}
          type="file"
          accept={accept}
          onChange={(e) => onChange(e.target.files[0])}
          className="hidden"
        />
      </label>
    </div>
  );
};



const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const validateFile = (file) => {
  if (file && file.size > MAX_FILE_SIZE) {
    alert(`File is too large. Maximum size is 10MB.`);
    return false;
  }
  return true;
};

const ProfileEditor = ({ user, onClose, onSaved, mode = 'edit', role = 'candidate', isAdmin = false }) => {
  const { t, language } = useLanguage();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(1);

  // Robust role check
  const userRole = user?.role?.toLowerCase() || '';
  const isPartner = userRole === 'sourcing_partner' || userRole === 'recruiting_partner';
  const isStaffOrAdmin = userRole === 'staff' || userRole === 'admin';

  let stepTitles = [];
  if (isPartner) {
    if (mode === 'company') {
      stepTitles = [
        { icon: Building2, title: 'Company Details' }
      ];
    } else {
      stepTitles = [
        { icon: User, title: t('dashboard.profile') || 'Profile' },
        { icon: Building2, title: t('roles.recruitingPartner') || 'Agency' }
      ];
    }
  } else if (isStaffOrAdmin) {
    stepTitles = [
      { icon: User, title: 'Basic' },
      { icon: Briefcase, title: 'Occupation' },
      { icon: Languages, title: 'Languages' },
      { icon: GraduationCap, title: 'Education' },
      { icon: Award, title: 'Skills' },
      { icon: FileText, title: 'Documents' }
    ];
  } else {
    stepTitles = [
      { icon: User, title: 'Basic' },
      { icon: Briefcase, title: 'Occupation' },
      { icon: Languages, title: 'Languages' },
      { icon: GraduationCap, title: 'Education' },
      { icon: Award, title: 'Skills' },
      { icon: FileText, title: 'Documents' }
    ];
  }

  const totalSteps = stepTitles.length;
  const { score: completionScore, missingFields } = userRole === 'candidate' ? calculateProfileCompletion(user) : { score: 0, missingFields: [] };

  // Parse helper functions
  const parseName = (name) => {
    if (!name) return { first_name: '', last_name: '' };
    const parts = name.trim().split(' ');
    if (parts.length === 1) return { first_name: parts[0], last_name: '' };
    return { first_name: parts[0], last_name: parts.slice(1).join(' ') };
  };

  const parseSkills = (skills) => {
    if (!skills) return [];
    if (Array.isArray(skills)) return skills;
    if (typeof skills === 'string') {
      try {
        const parsed = JSON.parse(skills);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        // Fallback for comma separated string
        return skills.split(',').map(s => s.trim()).filter(Boolean);
      }
    }
    return [];
  };

  const parseCertificates = (certs) => {
    if (!certs) return [];
    if (Array.isArray(certs)) return certs;
    if (typeof certs === 'string') {
      try {
        return JSON.parse(certs);
      } catch (e) {
        return [];
      }
    }
    return [];
  };

  const parseEducationHistory = (edu) => {
    if (!edu) return [];
    if (Array.isArray(edu)) return edu;
    if (typeof edu === 'string') {
      try {
        return JSON.parse(edu);
      } catch (e) {
        return [];
      }
    }
    return [];
  };

  const parseLanguageCertificates = (langCerts) => {
    if (!langCerts) return [];
    if (Array.isArray(langCerts)) return langCerts;
    if (typeof langCerts === 'string') {
      try {
        return JSON.parse(langCerts);
      } catch (e) {
        return [];
      }
    }
    return [];
  };

  const [form, setForm] = useState(() => {
    const nameParts = parseName(user?.name);
    return {
      // Step 1: Basic Info
      salutation: user?.salutation || '',
      first_name: user?.first_name || nameParts.first_name,
      last_name: user?.last_name || nameParts.last_name,
      email: user?.email || '',
      phone: user?.phone || '',

      // Partner Fields
      company_name: user?.company_name || '',
      address: user?.address || '',

      // Step 2: Occupation (Candidate only)
      occupation: user?.occupation || '',

      // Step 3: Language Certificates (Candidate only)
      language_certificates: parseLanguageCertificates(user?.language_certificates) || [],

      // Step 4: Education (Candidate only)
      education_history: parseEducationHistory(user?.education_history) || (user?.qualification ? [{
        degree: user.qualification || '',
        field_of_study: '',
        institution: user.university || '',
        start_date: '',
        end_date: user.year_of_passout ? `${user.year_of_passout}-01-01` : '',
        grade: '',
        description: user.experience || ''
      }] : []),

      // Step 5: Skills (Candidate only)
      skills: parseSkills(user?.skills) || [],

      // Step 6: Documents (Candidate only)
      resume: null,
      resume_url: user?.resume_url || null,
      photo: null,
      photo_url: user?.photo_url || null,

      profile_status: user?.profile_status || 'incomplete',
      certificates: parseCertificates(user?.certificates) || [],

      // Sourcing Partner Verification Fields
      legal_entity_name: user?.legal_entity_name || '',
      trade_name: user?.trade_name || '',
      company_website: user?.company_website || '',
      year_of_establishment: user?.year_of_establishment || '',
      company_size: user?.company_size || '',
      specialization: user?.specialization || '',
      account_manager_name: user?.account_manager_name || '',
      official_email: user?.official_email || '',
      direct_phone: user?.direct_phone || '',
      escalation_contact_name: user?.escalation_contact_name || '',
      escalation_contact_email: user?.escalation_contact_email || '',
      tax_id: user?.tax_id || '',
      incorporation_certificate: null,
      incorporation_certificate_url: user?.incorporation_certificate_url || null,
      labor_license: null,
      labor_license_url: user?.labor_license_url || null,
      msme_diversity_certificate_url: user?.msme_diversity_certificate_url || null,
      logo: null,
      logo_url: user?.logo_url || null,
    };
  });

  // Effect to update form if user prop changes (e.g. if modal is reused or data loads late)
  useEffect(() => {
    const nameParts = parseName(user?.name);
    setForm({
      salutation: user?.salutation || '',
      first_name: user?.first_name || nameParts.first_name,
      last_name: user?.last_name || nameParts.last_name,
      email: user?.email || '',
      phone: user?.phone || '',

      company_name: user?.company_name || '',
      address: user?.address || '',

      occupation: user?.occupation || '',
      language_certificates: parseLanguageCertificates(user?.language_certificates) || [],
      education_history: parseEducationHistory(user?.education_history) || (user?.qualification ? [{
        degree: user.qualification || '',
        field_of_study: '',
        institution: user.university || '',
        start_date: '',
        end_date: user.year_of_passout ? `${user.year_of_passout}-01-01` : '',
        grade: '',
        description: user.experience || ''
      }] : []),
      skills: parseSkills(user?.skills) || [],
      resume: null,
      resume_url: user?.resume_url || null,
      photo: null,
      photo_url: user?.photo_url || null,
      profile_status: user?.profile_status || 'incomplete',
      certificates: parseCertificates(user?.certificates) || [],

      legal_entity_name: user?.legal_entity_name || '',
      trade_name: user?.trade_name || '',
      company_website: user?.company_website || '',
      year_of_establishment: user?.year_of_establishment || '',
      company_size: user?.company_size || '',
      specialization: user?.specialization || '',
      account_manager_name: user?.account_manager_name || '',
      official_email: user?.official_email || '',
      direct_phone: user?.direct_phone || '',
      escalation_contact_name: user?.escalation_contact_name || '',
      escalation_contact_email: user?.escalation_contact_email || '',
      tax_id: user?.tax_id || '',
      incorporation_certificate: null,
      incorporation_certificate_url: user?.incorporation_certificate_url || null,
      labor_license: null,
      labor_license_url: user?.labor_license_url || null,
      msme_diversity_certificate: null,
      msme_diversity_certificate_url: user?.msme_diversity_certificate_url || null,
      logo: null,
      logo_url: user?.logo_url || null,
    });
    setStep(1); // Reset step when user changes
    setErrors({});
  }, [user]);

  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [canSubmit, setCanSubmit] = useState(true);

  // Safety delay to prevent accidental submission when reaching the final step
  useEffect(() => {
    if (step === totalSteps) {
      setCanSubmit(false);
      const timer = setTimeout(() => setCanSubmit(true), 800);
      return () => clearTimeout(timer);
    }
  }, [step, totalSteps]);

  // Clear resume error if resume exists (either file or URL)
  useEffect(() => {
    if (form.resume || form.resume_url) {
      setErrors(prev => {
        if (prev.resume) {
          const newErrors = { ...prev };
          delete newErrors.resume;
          return newErrors;
        }
        return prev;
      });
    }
  }, [form.resume, form.resume_url]);

  const next = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const prev = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const validateStep = (stepNum) => {
    const newErrors = {};

    if (stepNum === 1 && mode !== 'company') {
      if (!form.first_name?.trim()) newErrors.first_name = 'First name is required';
      if (!form.last_name?.trim()) newErrors.last_name = 'Last name is required';
      if (!form.email?.trim()) newErrors.email = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'Invalid email format';
      if (!form.phone?.trim()) newErrors.phone = 'Phone is required';
    }

    if (isPartner) {
      if (stepNum === 1 && mode !== 'company') {
        if (!form.first_name?.trim()) newErrors.first_name = 'First name is required';
        if (!form.last_name?.trim()) newErrors.last_name = 'Last name is required';
        if (!form.email?.trim()) newErrors.email = 'Email is required';
        if (!form.phone?.trim()) newErrors.phone = 'Phone is required';
      }
      if (stepNum === 2 || (mode === 'company' && stepNum === 1)) {
        if (userRole === 'sourcing_partner' || userRole === 'recruiting_partner') {
          if (!form.legal_entity_name?.trim()) newErrors.legal_entity_name = (t('partner.legalEntityName') || 'Legal Entity Name') + ' is required';
          if (!form.company_website?.trim()) newErrors.company_website = (t('partner.companyWebsite') || 'Company Website') + ' is required';
          if (!form.account_manager_name?.trim()) newErrors.account_manager_name = (t('partner.accountManagerName') || 'Account Manager Name') + ' is required';
          if (!form.official_email?.trim()) newErrors.official_email = (t('partner.officialEmail') || 'Official Email') + ' is required';
          if (!form.tax_id?.trim()) newErrors.tax_id = (t('partner.taxId') || 'Tax Identification Number') + ' is required';
        } else {
          if (!form.company_name?.trim()) newErrors.company_name = 'Agency/Company Name is required';
          if (!form.address?.trim()) newErrors.address = 'Address is required';
        }
      }
    } else {
      // Candidate validation
      if (stepNum === 2) {
        if (!form.occupation) newErrors.occupation = 'Occupation is required';
      }

      if (stepNum === 6) {
        // Resume is required - check if either new file or existing URL is present
        if (!form.resume && !form.resume_url) {
          newErrors.resume = 'Resume is required';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (step < totalSteps) {
      // Validate current step before moving to next
      if (validateStep(step)) {
        next();
      }
      return;
    }

    // Final submission - validate current step
    if (!validateStep(step)) {
      return;
    }

    // For candidates, resume is validated in validateStep(6)
    if (!isPartner && !isStaffOrAdmin && !form.resume && !form.resume_url) {
      setErrors({ resume: 'Please upload your resume' });
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();

      // Basic fields common to all — always include even in company mode to avoid backend overwriting with empty
      fd.append('first_name', form.first_name || user?.first_name || user?.name?.split(' ')[0] || '');
      fd.append('last_name', form.last_name || user?.last_name || user?.name?.split(' ').slice(1).join(' ') || '');
      fd.append('email', form.email || user?.email || '');
      fd.append('phone', form.phone || user?.phone || '');
      if (form.salutation || user?.salutation) fd.append('salutation', form.salutation || user?.salutation || '');
      if (form.photo) fd.append('photo', form.photo);

      if (isPartner) {
        if (userRole === 'sourcing_partner' || userRole === 'recruiting_partner') {
          fd.append('legal_entity_name', form.legal_entity_name);
          fd.append('trade_name', form.trade_name || '');
          fd.append('company_website', form.company_website);
          fd.append('year_of_establishment', form.year_of_establishment || '');
          fd.append('company_size', form.company_size || '');
          fd.append('specialization', form.specialization || '');
          fd.append('account_manager_name', form.account_manager_name);
          fd.append('official_email', form.official_email);
          fd.append('direct_phone', form.direct_phone || '');
          fd.append('escalation_contact_name', form.escalation_contact_name || '');
          fd.append('escalation_contact_email', form.escalation_contact_email || '');
          fd.append('tax_id', form.tax_id);
          if (form.logo) fd.append('logo', form.logo);
          if (form.logo_url && !form.logo) fd.append('logo_url', form.logo_url);

          if (form.incorporation_certificate) fd.append('incorporation_certificate', form.incorporation_certificate);
          else if (form.incorporation_certificate_url) fd.append('incorporation_certificate_url', form.incorporation_certificate_url);

          if (form.labor_license) fd.append('labor_license', form.labor_license);
          else if (form.labor_license_url) fd.append('labor_license_url', form.labor_license_url);

          if (form.msme_diversity_certificate) fd.append('msme_diversity_certificate', form.msme_diversity_certificate);
          else if (form.msme_diversity_certificate_url) fd.append('msme_diversity_certificate_url', form.msme_diversity_certificate_url);
        } else {
          fd.append('company_name', form.company_name);
          fd.append('address', form.address);
        }
      } else if (!isStaffOrAdmin) {
        // Candidate specific
        fd.append('occupation', form.occupation);
        if (form.qualification) fd.append('qualification', form.qualification);
        if (form.experience) fd.append('experience', form.experience);
        if (form.university) fd.append('university', form.university);
        if (form.year_of_passout) fd.append('year_of_passout', form.year_of_passout.toString());
        if (form.german_level) fd.append('german_level', form.german_level);
        if (form.profile_status) fd.append('profile_status', form.profile_status);

        fd.append('skills', JSON.stringify(form.skills || []));
        fd.append('education_history', JSON.stringify(form.education_history || []));
        fd.append('language_certificates', JSON.stringify(form.language_certificates || []));
        fd.append('certificates', JSON.stringify(form.certificates || []));

        if (form.resume) fd.append('resume', form.resume);

        form.certificates?.forEach((cert, index) => {
          if (cert.file) {
            fd.append(`certificate_${index}`, cert.file);
            if (cert.type) fd.append(`certificate_type_${index}`, cert.type);
          }
        });

        form.language_certificates?.forEach((cert, index) => {
          if (cert.certificate_file) fd.append(`language_cert_${index}`, cert.certificate_file);
        });
      }

      let endpoint;
      let method = 'put';

      if (mode === 'create') {
        endpoint = `/sourcing/candidates`;
        method = 'post';
      } else {
        if (role === 'admin') {
          endpoint = `/admin/users/${user.id}`;
        } else if (role === 'recruiting_partner') {
          endpoint = `/recruiting/profile`;
        } else if (role === 'sourcing_partner') {
          endpoint = isPartner ? `/sourcing/profile` : `/sourcing/candidates/${user.id}`;
        } else {
          endpoint = `/staff/users/${user.id}`;
        }
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);

      try {
        await api[method](endpoint, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          signal: controller.signal
        });
        if (onSaved) await onSaved();
        if (onClose) onClose();
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (err) {
      console.error('Profile update error', err);
      let errorMessage = err.response?.data?.error || err.message || 'Failed to update profile';
      let errorDetails = err.response?.data?.details ? `\n\nTechnical Details: ${err.response.data.details}` : '';
      alert(`❌ ${errorMessage}${errorDetails}`);
    } finally {
      setSubmitting(false);
    }
  };

  const addLanguageCertificate = () => {
    setForm({
      ...form,
      language_certificates: [...(form.language_certificates || []), {
        language: '',
        level: '',
        issuer: '',
        issued_date: '',
        certificate_file: null,
        modules_count: '',
        lesen: false,
        hoeren: false,
        schreiben: false,
        sprechen: false,
        passing_date: ''
      }]
    });
  };

  const removeLanguageCertificate = (index) => {
    setForm({
      ...form,
      language_certificates: form.language_certificates.filter((_, i) => i !== index)
    });
  };

  const updateLanguageCertificate = (index, field, value) => {
    const updated = [...form.language_certificates];
    updated[index] = { ...updated[index], [field]: value };
    setForm({ ...form, language_certificates: updated });
  };

  const addEducation = () => {
    setForm({
      ...form,
      education_history: [...(form.education_history || []), {
        level: '',
        degree: '',
        field_of_study: '',
        institution: '',
        start_date: '',
        end_date: '',
        grade: '',
        description: ''
      }]
    });
  };

  const removeEducation = (index) => {
    setForm({
      ...form,
      education_history: form.education_history.filter((_, i) => i !== index)
    });
  };

  const updateEducation = (index, field, value) => {
    const updated = [...form.education_history];
    updated[index] = { ...updated[index], [field]: value };
    setForm({ ...form, education_history: updated });
  };

  const addSkill = () => {
    const name = prompt('Enter skill name:');
    if (name && name.trim()) {
      const proficiency = prompt('Enter proficiency (e.g., Beginner, Intermediate, Expert):', 'Intermediate');
      const years = prompt('Enter years of experience:', '1');

      const newSkill = {
        name: name.trim(),
        proficiency: proficiency || 'Intermediate',
        years_of_experience: years || '1'
      };

      setForm({
        ...form,
        skills: [...(form.skills || []), newSkill]
      });
    }
  };

  const removeSkill = (index) => {
    setForm({
      ...form,
      skills: form.skills.filter((_, i) => i !== index)
    });
  };

  const addCertificate = () => {
    setForm({
      ...form,
      certificates: [...(form.certificates || []), {
        name: '',
        issuer: '',
        issued_date: '',
        expiry_date: '',
        file: null,
        type: 'qualification'
      }]
    });
  };

  const removeCertificate = (index) => {
    setForm({
      ...form,
      certificates: form.certificates.filter((_, i) => i !== index)
    });
  };

  const updateCertificate = (index, field, value) => {
    const updated = [...form.certificates];
    updated[index] = { ...updated[index], [field]: value };
    setForm({ ...form, certificates: updated });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8 animate-in fade-in duration-500">
      {/* Profile Completion Overview (For Candidates) */}
      {userRole === 'candidate' && (
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <Info className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-800 uppercase tracking-tight">Profile Integrity</h3>
                <p className="text-[10px] font-medium text-slate-500">Current completion status and requirements</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xl font-black ${completionScore > 80 ? 'text-blue-600' : completionScore > 50 ? 'text-indigo-600' : 'text-slate-400'}`}>
                {completionScore}%
              </span>
            </div>
          </div>
          <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-1000 ease-out shadow-sm ${completionScore > 80 ? 'bg-blue-500' : completionScore > 50 ? 'bg-indigo-500' : 'bg-slate-400'
                }`}
              style={{ width: `${completionScore}%` }}
            />
          </div>

          {missingFields.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {missingFields.slice(0, 5).map((field, i) => (
                <span key={i} className="px-2 py-0.5 bg-white border border-slate-200 rounded-md text-[9px] font-bold text-slate-500 uppercase tracking-wider">
                  {field.replace(/_/g, ' ')}
                </span>
              ))}
              {missingFields.length > 5 && (
                <span className="text-[9px] font-bold text-slate-400 px-1">+{missingFields.length - 5} more</span>
              )}
            </div>
          )}

          {/* Admin Override Toggle */}
          {(role === 'staff' || role === 'admin') && (
            <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={`p-1.5 rounded-lg ${form.profile_status === 'complete' ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'}`}>
                  <Check className="h-4 w-4" />
                </div>
                <span className="text-[11px] font-bold text-slate-700 uppercase tracking-tight">Manual Completion Override</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={form.profile_status === 'complete'}
                  onChange={(e) => setForm({ ...form, profile_status: e.target.checked ? 'complete' : 'incomplete' })}
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          )}
        </div>
      )}

      {/* Modern Progress Steps */}
      <div className="relative pt-2 px-2">
        <div className="flex items-center justify-between relative z-10">
          {stepTitles.map((stepTitle, idx) => {
            const StepIcon = stepTitle.icon;
            const isActive = step === idx + 1;
            const isCompleted = step > idx + 1;

            return (
              <div key={idx} className="flex flex-col items-center gap-3">
                <div
                  className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-300 border-2 ${isActive
                    ? 'bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-200 scale-110'
                    : isCompleted
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'bg-white border-slate-200 text-slate-400'
                    }`}
                >
                  {isCompleted ? <Check className="h-6 w-6" /> : <StepIcon className="h-5 w-5" />}
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-widest ${isActive ? 'text-blue-600' : 'text-slate-400'}`}>
                  {stepTitle.title}
                </span>
              </div>
            );
          })}
        </div>
        {/* Connector Line */}
        <div className="absolute top-[24px] left-0 right-0 h-0.5 bg-slate-100 -z-0 mx-10" />
        <div
          className="absolute top-[24px] left-0 h-0.5 bg-blue-500 -z-0 transition-all duration-500 ease-in-out mx-10"
          style={{ width: `${((step - 1) / (totalSteps - 1)) * 100}%` }}
        />
      </div>

      {/* Step 1: Basic Information */}
      {step === 1 && mode !== 'company' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Salutation</label>
            <select
              value={form.salutation}
              onChange={(e) => setForm({ ...form, salutation: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-slate-900"
            >
              <option value="">Select</option>
              <option value="Mr.">Mr.</option>
              <option value="Mrs.">Mrs.</option>
              <option value="Ms.">Ms.</option>
              <option value="Dr.">Dr.</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">First Name *</label>
              <input
                type="text"
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-slate-900 ${errors.first_name ? 'border-red-500' : 'border-slate-300'
                  }`}
                required
              />
              {errors.first_name && <p className="text-red-500 text-sm mt-1">{errors.first_name}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Last Name *</label>
              <input
                type="text"
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-slate-900 ${errors.last_name ? 'border-red-500' : 'border-slate-300'
                  }`}
                required
              />
              {errors.last_name && <p className="text-red-500 text-sm mt-1">{errors.last_name}</p>}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-slate-900 ${errors.email ? 'border-red-500' : 'border-slate-300'
                }`}
              required
            />
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Phone *</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-slate-900 ${errors.phone ? 'border-red-500' : 'border-slate-300'
                }`}
              required
            />
            {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
          </div>

          {/* German Level only for candidates */}
          {!isPartner && !isStaffOrAdmin && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">German Level</label>
              <select
                value={form.german_level}
                onChange={(e) => setForm({ ...form, german_level: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-slate-900"
              >
                <option value="">Select Level</option>
                {LANGUAGE_LEVELS.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>
          )}

          {/* Photo Upload for Partners (In Step 1) */}
          {isPartner && (
            <div className="pt-4 border-t border-slate-100">
              <UploadCard
                label="Profile / Firm Photo"
                file={form.photo}
                existingUrl={form.photo_url}
                onChange={(file) => setForm({ ...form, photo: file })}
                accept="image/*"
              />
            </div>
          )}
        </div>
      )}

      {/* Partner Specific Steps */}
      {isPartner && ((mode === 'company' && step === 1) || (mode !== 'company' && step === 2)) && (
        <div className="space-y-8">
          <>
            {/* 1. Basic Company Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-slate-800 border-b border-slate-100 pb-2">1. Basic Company Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Legal Entity Name *</label>
                  <input
                    type="text"
                    value={form.legal_entity_name}
                    onChange={(e) => setForm({ ...form, legal_entity_name: e.target.value })}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-slate-900 ${errors.legal_entity_name ? 'border-red-500' : 'border-slate-300'}`}
                    placeholder="e.g., ABC Staffing Solutions Pvt Ltd"
                    required
                  />
                  {errors.legal_entity_name && <p className="text-red-500 text-sm mt-1">{errors.legal_entity_name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Trade Name/Brand Name</label>
                  <input
                    type="text"
                    value={form.trade_name}
                    onChange={(e) => setForm({ ...form, trade_name: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-slate-900"
                    placeholder="If different from legal name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Company Website *</label>
                  <input
                    type="url"
                    value={form.company_website}
                    onChange={(e) => setForm({ ...form, company_website: e.target.value })}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-slate-900 ${errors.company_website ? 'border-red-500' : 'border-slate-300'}`}
                    placeholder="https://www.example.com"
                    required
                  />
                  {errors.company_website && <p className="text-red-500 text-sm mt-1">{errors.company_website}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('partner.yearOfEstablishment') || 'Year of Establishment'}</label>
                  <input
                    type="text"
                    value={form.year_of_establishment}
                    onChange={(e) => setForm({ ...form, year_of_establishment: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-slate-900"
                    placeholder="e.g., 2010"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('partner.companySize') || 'Company Size'}</label>
                  <input
                    type="text"
                    value={form.company_size}
                    onChange={(e) => setForm({ ...form, company_size: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-slate-900"
                    placeholder="Number of recruiters/employees"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('partner.specialization') || 'Specialization'}</label>
                  <input
                    type="text"
                    value={form.specialization}
                    onChange={(e) => setForm({ ...form, specialization: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-slate-900"
                    placeholder="e.g., IT, Healthcare, Executive Search"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Corporate Address (Headquarters)</label>
                <textarea
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-slate-900"
                  placeholder="Full corporate address..."
                />
              </div>
            </div>

            {/* 2. Point of Contact (SPOC) Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-slate-800 border-b border-slate-100 pb-2">2. {t('partner.pointOfContact') || 'Point of Contact (SPOC) Details'}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('partner.accountManagerName') || 'Account Manager Name'} *</label>
                  <input
                    type="text"
                    value={form.account_manager_name}
                    onChange={(e) => setForm({ ...form, account_manager_name: e.target.value })}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-slate-900 ${errors.account_manager_name ? 'border-red-500' : 'border-slate-300'}`}
                    placeholder="Primary contact name"
                    required
                  />
                  {errors.account_manager_name && <p className="text-red-500 text-sm mt-1">{errors.account_manager_name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Official Email Address *</label>
                  <input
                    type="email"
                    value={form.official_email}
                    onChange={(e) => setForm({ ...form, official_email: e.target.value })}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-slate-900 ${errors.official_email ? 'border-red-500' : 'border-slate-300'}`}
                    placeholder="e.g., @abcstaffing.com"
                    required
                  />
                  {errors.official_email && <p className="text-red-500 text-sm mt-1">{errors.official_email}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Direct Phone/Mobile Number</label>
                  <input
                    type="tel"
                    value={form.direct_phone}
                    onChange={(e) => setForm({ ...form, direct_phone: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-slate-900"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Escalation Contact Name</label>
                  <input
                    type="text"
                    value={form.escalation_contact_name}
                    onChange={(e) => setForm({ ...form, escalation_contact_name: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-slate-900"
                    placeholder="Senior manager name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Escalation Contact Email</label>
                  <input
                    type="email"
                    value={form.escalation_contact_email}
                    onChange={(e) => setForm({ ...form, escalation_contact_email: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-slate-900"
                  />
                </div>
              </div>
            </div>

            {/* 3. Statutory & Compliance Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-slate-800 border-b border-slate-100 pb-2">3. {t('partner.complianceDocuments') || 'Statutory & Compliance Details'}</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">{t('partner.taxId') || 'Tax Identification Number'} *</label>
                <input
                  type="text"
                  value={form.tax_id}
                  onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-slate-900 ${errors.tax_id ? 'border-red-500' : 'border-slate-300'}`}
                  placeholder="e.g., PAN, GSTIN, TIN/EIN"
                  required
                />
                {errors.tax_id && <p className="text-red-500 text-sm mt-1">{errors.tax_id}</p>}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <UploadCard
                  label={t('partner.incorporationCertificate') || 'Incorporation Certificate'}
                  file={form.incorporation_certificate}
                  onChange={(file) => setForm({ ...form, incorporation_certificate: file })}
                  existingUrl={form.incorporation_certificate_url}
                />
                <UploadCard
                  label={t('partner.laborLicense') || 'Labor License'}
                  file={form.labor_license}
                  onChange={(file) => setForm({ ...form, labor_license: file })}
                  existingUrl={form.labor_license_url}
                />
                <UploadCard
                  label={t('partner.msmeCertificate') || 'MSME/Diversity Cert.'}
                  file={form.msme_diversity_certificate}
                  onChange={(file) => setForm({ ...form, msme_diversity_certificate: file })}
                  existingUrl={form.msme_diversity_certificate_url}
                />
                <UploadCard
                  label={t('partner.logo') || 'Firm Logo'}
                  file={form.logo}
                  onChange={(file) => setForm({ ...form, logo: file })}
                  existingUrl={form.logo_url}
                  accept="image/*"
                />
              </div>
            </div>
          </>
        </div>
      )
      }

      {/* Candidate Steps (Only if not partner) */}
      {/* Candidate Steps (Only if not partner and not staff/admin) */}
      {
        !isPartner && !isStaffOrAdmin && (
          <>
            {/* Step 2: Occupation */}
            {step === 2 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Occupation *</label>
                  <select
                    value={form.occupation}
                    onChange={(e) => setForm({ ...form, occupation: e.target.value })}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-slate-900 ${errors.occupation ? 'border-red-500' : 'border-slate-300'
                      }`}
                    required
                  >
                    <option value="">Select Occupation</option>
                    {OCCUPATIONS.map(occ => (
                      <option key={occ} value={occ}>{occ}</option>
                    ))}
                  </select>
                  {errors.occupation && <p className="text-red-500 text-sm mt-1">{errors.occupation}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Qualification</label>
                  <input
                    type="text"
                    value={form.qualification}
                    onChange={(e) => setForm({ ...form, qualification: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-slate-900"
                    placeholder="e.g., B.Sc., M.Sc., Diploma"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Experience</label>
                  <textarea
                    value={form.experience}
                    onChange={(e) => setForm({ ...form, experience: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-slate-900"
                    placeholder="Describe your work experience..."
                  />
                </div>
              </div>
            )}

            {/* Step 3: Language Certificates */}
            {step === 3 && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-slate-800">Language Certificates</h3>
                  <button
                    type="button"
                    onClick={addLanguageCertificate}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    + Add Certificate
                  </button>
                </div>
                {form.language_certificates?.map((cert, index) => (
                  <div key={index} className="border border-slate-300 rounded-lg p-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium text-slate-700">Certificate {index + 1}</h4>
                      <button
                        type="button"
                        onClick={() => removeLanguageCertificate(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Language</label>
                        <input
                          type="text"
                          value={cert.language || ''}
                          onChange={(e) => updateLanguageCertificate(index, 'language', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-900"
                          placeholder="e.g., German"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Level</label>
                        <select
                          value={cert.level || ''}
                          onChange={(e) => updateLanguageCertificate(index, 'level', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-900"
                        >
                          <option value="">Select Level</option>
                          {LANGUAGE_LEVELS.map(level => (
                            <option key={level} value={level}>{level}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Issuer</label>
                        <input
                          type="text"
                          value={cert.issuer || ''}
                          onChange={(e) => updateLanguageCertificate(index, 'issuer', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-900"
                          placeholder="e.g., Goethe Institute"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Issued Date</label>
                        <input
                          type="date"
                          value={cert.issued_date || ''}
                          onChange={(e) => updateLanguageCertificate(index, 'issued_date', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-900"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Pass Date</label>
                        <input
                          type="date"
                          value={cert.passing_date || ''}
                          onChange={(e) => updateLanguageCertificate(index, 'passing_date', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-900"
                        />
                      </div>
                      <div className="col-span-2">
                        <UploadCard
                          label="Certificate File"
                          file={cert.certificate_file}
                          onChange={(file) => {
                            if (validateFile(file)) {
                              updateLanguageCertificate(index, 'certificate_file', file);
                            }
                          }}
                          existingUrl={cert.certificate_url || cert.file_url}
                        />
                      </div>
                    </div>

                    {/* Language Skills Section */}
                    <div className="mt-4 border-t border-slate-100 pt-4">
                      <label className="block text-sm font-bold text-slate-700 mb-3">Proficiency Levels</label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Listening</label>
                          <select
                            value={cert.listening_level || cert.hoeren_level || ''}
                            onChange={(e) => updateLanguageCertificate(index, 'listening_level', e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-900 text-sm"
                          >
                            <option value="">Select</option>
                            {LANGUAGE_LEVELS.map(level => (
                              <option key={level} value={level}>{level}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Reading</label>
                          <select
                            value={cert.reading_level || cert.lesen_level || ''}
                            onChange={(e) => updateLanguageCertificate(index, 'reading_level', e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-900 text-sm"
                          >
                            <option value="">Select</option>
                            {LANGUAGE_LEVELS.map(level => (
                              <option key={level} value={level}>{level}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Speaking</label>
                          <select
                            value={cert.speaking_level || cert.sprechen_level || ''}
                            onChange={(e) => updateLanguageCertificate(index, 'speaking_level', e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-900 text-sm"
                          >
                            <option value="">Select</option>
                            {LANGUAGE_LEVELS.map(level => (
                              <option key={level} value={level}>{level}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Writing</label>
                          <select
                            value={cert.writing_level || cert.schreiben_level || ''}
                            onChange={(e) => updateLanguageCertificate(index, 'writing_level', e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-900 text-sm"
                          >
                            <option value="">Select</option>
                            {LANGUAGE_LEVELS.map(level => (
                              <option key={level} value={level}>{level}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {(!form.language_certificates || form.language_certificates.length === 0) && (
                  <p className="text-slate-500 text-center py-4">No language certificates added yet</p>
                )}
              </div>
            )}

            {/* Step 4: Education */}
            {step === 4 && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-slate-800">Education History</h3>
                  <button
                    type="button"
                    onClick={addEducation}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    + Add Education
                  </button>
                </div>
                {form.education_history?.map((edu, index) => (
                  <div key={index} className="border border-slate-300 rounded-lg p-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium text-slate-700">Education {index + 1}</h4>
                      <button
                        type="button"
                        onClick={() => removeEducation(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Qualification Level</label>
                        <select
                          value={edu.level || ''}
                          onChange={(e) => updateEducation(index, 'level', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-900"
                        >
                          <option value="">Select Level</option>
                          <option value="10th Standard">10th Standard</option>
                          <option value="12th Standard">12th Standard</option>
                          <option value="Diploma">Diploma</option>
                          <option value="Bachelors Degree">Bachelor's Degree</option>
                          <option value="Masters Degree">Master's Degree</option>
                          <option value="PhD">PhD</option>
                          <option value="Vocational Training">Vocational Training</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Degree / Major</label>
                        <input
                          type="text"
                          value={edu.degree || ''}
                          onChange={(e) => updateEducation(index, 'degree', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-900"
                          placeholder="e.g., B.Sc., Science, Arts"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Field of Study</label>
                        <input
                          type="text"
                          value={edu.field_of_study || ''}
                          onChange={(e) => updateEducation(index, 'field_of_study', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-900"
                          placeholder="e.g., Computer Science"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Institution</label>
                        <input
                          type="text"
                          value={edu.institution || ''}
                          onChange={(e) => updateEducation(index, 'institution', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-900"
                          placeholder="e.g., University of Berlin"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Grade</label>
                        <input
                          type="text"
                          value={edu.grade || ''}
                          onChange={(e) => updateEducation(index, 'grade', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-900"
                          placeholder="e.g., 2.5 or A"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Start Date</label>
                        <input
                          type="date"
                          value={edu.start_date || ''}
                          onChange={(e) => updateEducation(index, 'start_date', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-900"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">End Date</label>
                        <input
                          type="date"
                          value={edu.end_date || ''}
                          onChange={(e) => updateEducation(index, 'end_date', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-900"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Passing Date</label>
                        <input
                          type="date"
                          value={edu.passing_date || ''}
                          onChange={(e) => updateEducation(index, 'passing_date', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-900"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Roll No / Certificate Number</label>
                        <input
                          type="text"
                          value={edu.roll_no || ''}
                          onChange={(e) => updateEducation(index, 'roll_no', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-900"
                          placeholder="e.g., 123456"
                        />
                      </div>
                    </div>
                    <div className="w-full">
                      <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                      <textarea
                        value={edu.description || ''}
                        onChange={(e) => updateEducation(index, 'description', e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-900"
                        placeholder="Additional details..."
                      />
                    </div>
                  </div>
                ))}
                {(!form.education_history || form.education_history.length === 0) && (
                  <p className="text-slate-500 text-center py-4">No education history added yet</p>
                )}
              </div>
            )}

            {/* Step 5: Skills */}
            {step === 5 && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-slate-800">Skills</h3>
                  <button
                    type="button"
                    onClick={addSkill}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    + Add Skill
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {form.skills?.map((skill, index) => (
                    <div key={index} className="bg-slate-100 px-3 py-2 rounded-xl flex items-center justify-between min-w-[150px] border border-slate-200">
                      <div className="flex flex-col">
                        <span className="font-bold text-slate-800">{typeof skill === 'object' ? skill.name : skill}</span>
                        {typeof skill === 'object' && (
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[10px] text-blue-600 font-bold uppercase">{skill.proficiency}</span>
                            <span className="text-[10px] text-slate-400">• {skill.years_of_experience} yrs</span>
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeSkill(index)}
                        className="text-slate-400 hover:text-red-500 p-1 rounded-full hover:bg-white transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  {(!form.skills || form.skills.length === 0) && (
                    <p className="text-slate-500 text-center w-full py-4">No skills added yet</p>
                  )}
                </div>
              </div>
            )}

            {/* Step 6: Documents */}
            {step === 6 && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Documents</h3>

                <UploadCard
                  label="Profile Photo"
                  file={form.photo}
                  onChange={(file) => {
                    if (validateFile(file)) {
                      setForm({ ...form, photo: file });
                    }
                  }}
                  existingUrl={form.photo_url}
                  accept=".jpg,.jpeg,.png"
                />

                <UploadCard
                  label="Resume / CV"
                  required
                  file={form.resume}
                  onChange={(file) => {
                    if (validateFile(file)) {
                      setForm({ ...form, resume: file });
                    }
                  }}
                  existingUrl={form.resume_url}
                />

                {/* Additional Certificates */}
                <div className="space-y-4 pt-4 border-t border-slate-200">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-slate-800">Additional Certificates</h3>
                    <button
                      type="button"
                      onClick={addCertificate}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      + Add Certificate
                    </button>
                  </div>

                  {form.certificates?.map((cert, index) => (
                    <div key={index} className="border border-slate-300 rounded-lg p-4 space-y-3">
                      <div className="flex justify-between items-center">
                        <h4 className="font-medium text-slate-700">Certificate {index + 1}</h4>
                        <button
                          type="button"
                          onClick={() => removeCertificate(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                          <input
                            type="text"
                            value={cert.name || ''}
                            onChange={(e) => updateCertificate(index, 'name', e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-900"
                            placeholder="Certificate Name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                          <select
                            value={cert.type || 'qualification'}
                            onChange={(e) => updateCertificate(index, 'type', e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-900"
                          >
                            <option value="qualification">Qualification</option>
                            <option value="experience">Experience</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Issuer</label>
                          <input
                            type="text"
                            value={cert.issuer || ''}
                            onChange={(e) => updateCertificate(index, 'issuer', e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-900"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
                          <input
                            type="date"
                            value={cert.issued_date || ''}
                            onChange={(e) => updateCertificate(index, 'issued_date', e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-900"
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-slate-700 mb-1">File</label>
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (validateFile(file)) {
                                updateCertificate(index, 'file', file);
                              }
                            }}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-900"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )
      }

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between pt-10 border-t border-slate-100">
        <button
          type="button"
          onClick={prev}
          disabled={step === 1 || submitting}
          className={`group flex items-center space-x-2 px-6 py-2.5 rounded-xl transition-all font-bold text-sm ${step === 1 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'
            }`}
        >
          <ChevronLeft className={`h-5 w-5 transition-transform group-hover:-translate-x-1 ${step === 1 ? '' : 'text-slate-400'}`} />
          <span>Back</span>
        </button>

        {step < totalSteps ? (
          <button
            key="next-btn"
            type="button"
            onClick={() => validateStep(step) && next()}
            className="group flex items-center space-x-2 px-8 py-2.5 bg-slate-800 text-white rounded-xl hover:bg-slate-900 transition-all shadow-lg shadow-slate-200 active:scale-95 font-bold text-sm"
          >
            <span>Continue</span>
            <ChevronRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </button>
        ) : (
          <button
            key="submit-btn"
            type="submit"
            disabled={submitting || !canSubmit}
            className={`group flex items-center space-x-2 px-10 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:shadow-xl hover:shadow-blue-500/20 transition-all active:scale-95 font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white" />
                <span>Synchronizing...</span>
              </>
            ) : (
              <>
                <Check className="h-5 w-5" />
                <span>Finalize Profile</span>
              </>
            )}
          </button>
        )}
      </div>
    </form >
  );
};

export default ProfileEditor;
